import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { MapIcon, StarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ToursReport = ({ data, dateRange }) => {
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Make sure data exists and has the expected structure
  // Handle both API response format and placeholder data format
  const reportData = data?.data || data || {};
  
  // Ensure tours_by_bookings is an array
  const toursByBookingsArray = Array.isArray(reportData.tours_by_bookings) ? reportData.tours_by_bookings : [];

  // Tours by bookings chart data
  const toursByBookingsData = {
    labels: toursByBookingsArray.map(item => item.tour_name || 'Unknown Tour'),
    datasets: [
      {
        label: 'Number of Bookings',
        data: toursByBookingsArray.map(item => item.bookings || 0),
        backgroundColor: 'rgba(79, 70, 229, 0.7)',
        borderColor: 'rgba(79, 70, 229, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Ensure tours_by_revenue is an array
  const toursByRevenueArray = Array.isArray(reportData.tours_by_revenue) ? reportData.tours_by_revenue : [];

  // Tours by revenue chart data
  const toursByRevenueData = {
    labels: toursByRevenueArray.map(item => item.tour_name || 'Unknown Tour'),
    datasets: [
      {
        label: 'Revenue (USD)',
        data: toursByRevenueArray.map(item => item.revenue || 0),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Ensure tours_by_rating is an array
  const toursByRatingArray = Array.isArray(reportData.tours_by_rating) ? reportData.tours_by_rating : [];

  // Tours by rating chart data
  const toursByRatingData = {
    labels: toursByRatingArray.map(item => item.tour_name || 'Unknown Tour'),
    datasets: [
      {
        label: 'Average Rating',
        data: toursByRatingArray.map(item => item.rating || 0),
        backgroundColor: 'rgba(245, 158, 11, 0.7)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const bookingsChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Tours by Number of Bookings',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0
        }
      }
    }
  };

  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Tours by Revenue',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const ratingChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Tours by Average Rating',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 0.5
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Tours Report</h2>
        <p className="text-sm text-gray-500 mb-6">
          Tour data from {formatDate(dateRange.startDate)} to {formatDate(dateRange.endDate)}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Tours</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.total_tours || 0}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Tours</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.active_tours || 0}</div>
                    <div className="text-sm text-gray-500">
                      {reportData.total_tours ? Math.round(((reportData.active_tours || 0) / reportData.total_tours) * 100) : 0}% of total
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <StarIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Most Booked Tour</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.most_booked_tour || 'N/A'}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Least Booked Tour</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.least_booked_tour || 'N/A'}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="space-y-8">
        {/* Tours by Bookings Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tours by Bookings</h3>
          <div className="h-80">
            <Bar data={toursByBookingsData} options={bookingsChartOptions} />
          </div>
        </div>

        {/* Tours by Revenue Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tours by Revenue</h3>
          <div className="h-80">
            <Bar data={toursByRevenueData} options={revenueChartOptions} />
          </div>
        </div>

        {/* Tours by Rating Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tours by Rating</h3>
          <div className="h-80">
            <Bar data={toursByRatingData} options={ratingChartOptions} />
          </div>
        </div>
      </div>

      {/* Tours by Bookings Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tours by Bookings</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tour Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg. Rating
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(toursByBookingsArray) ? toursByBookingsArray.map((tour, index) => {
                  // Find corresponding revenue and rating data
                  const revenueData = toursByRevenueArray.find(item => item.tour_name === tour.tour_name) || {};
                  const ratingData = toursByRatingArray.find(item => item.tour_name === tour.tour_name) || {};
                  
                  return (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {tour.tour_name || 'Unknown Tour'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {tour.bookings || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {formatCurrency(revenueData.revenue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {ratingData.rating ? ratingData.rating.toFixed(1) : 'N/A'}
                      </td>
                    </tr>
                  );
                }) : <tr><td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No tour data available</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToursReport;
